import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  CheckCircle, XCircle, Clock, UserPlus, Users, Target,
  Loader2, RefreshCcw, Eye, MessageSquare, Phone, Mail,
  Briefcase, Crown, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiUrl';

const ApprovalsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [approvals, setApprovals] = useState([]);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [actionType, setActionType] = useState('approve');
  const [remarks, setRemarks] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchApprovals = useCallback(async (status = null) => {
    setLoading(true);
    try {
      let url = `${API_URL}/target-definition/approvals`;
      if (status) url += `?status=${status}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setApprovals(data);
      }
    } catch (err) {
      console.error('Error fetching approvals:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handleAction = async () => {
    if (!selectedApproval) return;
    
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/target-definition/approvals/${selectedApproval.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: actionType,
          remarks: remarks
        }),
      });
      
      if (res.ok) {
        toast.success(`Request ${actionType}d successfully`);
        setShowActionDialog(false);
        setSelectedApproval(null);
        setRemarks('');
        fetchApprovals();
      } else {
        const err = await res.json();
        toast.error(err.detail || `Failed to ${actionType} request`);
      }
    } catch (err) {
      toast.error('Error processing request');
    } finally {
      setProcessing(false);
    }
  };

  const openActionDialog = (approval, action) => {
    setSelectedApproval(approval);
    setActionType(action);
    setRemarks('');
    setShowActionDialog(true);
  };

  const openDetailsDialog = (approval) => {
    setSelectedApproval(approval);
    setShowDetailsDialog(true);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'new_client': return <UserPlus className="w-4 h-4 text-blue-400" />;
      case 'new_dealer': return <Briefcase className="w-4 h-4 text-green-400" />;
      case 'new_rm': return <Crown className="w-4 h-4 text-purple-400" />;
      case 'target_override': return <Target className="w-4 h-4 text-amber-400" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'new_client': return 'New Client';
      case 'new_dealer': return 'New Dealer';
      case 'new_rm': return 'New RM';
      case 'target_override': return 'Target Override';
      default: return type;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'new_client': 
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">New Client</Badge>;
      case 'new_dealer': 
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">New Dealer</Badge>;
      case 'new_rm': 
        return <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">New RM</Badge>;
      case 'target_override': 
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Target Override</Badge>;
      default: 
        return <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">{type}</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'rm':
        return <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">RM</Badge>;
      case 'dealer':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Dealer</Badge>;
      default:
        return <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">{role}</Badge>;
    }
  };

  // Group approvals by type
  const newClientApprovals = approvals.filter(a => a.type === 'new_client');
  const newDealerApprovals = approvals.filter(a => a.type === 'new_dealer' || a.type === 'new_rm');
  const targetOverrideApprovals = approvals.filter(a => a.type === 'target_override');

  const pendingCount = approvals.filter(a => a.status === 'pending').length;
  const pendingClientCount = newClientApprovals.filter(a => a.status === 'pending').length;
  const pendingDealerCount = newDealerApprovals.filter(a => a.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="approvals-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <CheckCircle className="w-7 h-7 text-[#C5A059]" />
            Approvals
          </h1>
          <p className="text-slate-400">
            Review and process pending approval requests
            {pendingCount > 0 && (
              <Badge className="ml-2 bg-amber-500/20 text-amber-400 border-amber-500/30">{pendingCount} pending</Badge>
            )}
          </p>
        </div>
        <Button variant="outline" onClick={() => fetchApprovals()} className="border-[#C5A059]/30 hover:bg-[#C5A059]/10">
          <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="card-dark">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <UserPlus className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{pendingClientCount}</p>
                <p className="text-xs text-slate-400">Pending Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <Users className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{pendingDealerCount}</p>
                <p className="text-xs text-slate-400">Pending Dealers/RMs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{pendingCount}</p>
                <p className="text-xs text-slate-400">Total Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs by Type */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList className="bg-slate-800/50 border border-slate-700">
          <TabsTrigger value="all" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-black">
            All ({approvals.length})
          </TabsTrigger>
          <TabsTrigger value="new_client" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <UserPlus className="w-4 h-4 mr-1" />
            New Clients ({newClientApprovals.length})
          </TabsTrigger>
          <TabsTrigger value="new_dealer" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
            <Briefcase className="w-4 h-4 mr-1" />
            New Dealers/RMs ({newDealerApprovals.length})
          </TabsTrigger>
          <TabsTrigger value="target_override" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <Target className="w-4 h-4 mr-1" />
            Target Overrides ({targetOverrideApprovals.length})
          </TabsTrigger>
        </TabsList>

        {['all', 'new_client', 'new_dealer', 'target_override'].map((tab) => {
          let filteredApprovals;
          if (tab === 'all') {
            filteredApprovals = approvals;
          } else if (tab === 'new_dealer') {
            filteredApprovals = approvals.filter(a => a.type === 'new_dealer' || a.type === 'new_rm');
          } else {
            filteredApprovals = approvals.filter(a => a.type === tab);
          }

          return (
            <TabsContent key={tab} value={tab}>
              <Card className="card-dark border-white/5">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead className="text-slate-400">Type</TableHead>
                        <TableHead className="text-slate-400">Details</TableHead>
                        <TableHead className="text-slate-400">Requested By</TableHead>
                        <TableHead className="text-slate-400">Date</TableHead>
                        <TableHead className="text-slate-400">Status</TableHead>
                        <TableHead className="text-right text-slate-400">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredApprovals.map((approval) => (
                        <TableRow key={approval.id} className="border-white/5 hover:bg-white/5">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getTypeIcon(approval.type)}
                              {getTypeBadge(approval.type)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              {approval.type === 'new_client' && approval.data && (
                                <>
                                  <div className="text-white font-medium">
                                    {approval.data.client_name || 'N/A'}
                                  </div>
                                  <div className="text-xs text-slate-400 flex items-center gap-2">
                                    <code className="text-[#C5A059] bg-[#C5A059]/10 px-1 rounded">
                                      {approval.data.client_code || 'N/A'}
                                    </code>
                                    {approval.data.contact_number && (
                                      <span className="flex items-center gap-1">
                                        <Phone className="w-3 h-3" />
                                        {approval.data.contact_number}
                                      </span>
                                    )}
                                  </div>
                                </>
                              )}
                              {(approval.type === 'new_dealer' || approval.type === 'new_rm') && approval.data && (
                                <>
                                  <div className="text-white font-medium">
                                    {approval.data.dealer_name || approval.data.name || 'N/A'}
                                  </div>
                                  <div className="text-xs text-slate-400">
                                    {approval.data.dealer_code && (
                                      <code className="text-green-400 bg-green-500/10 px-1 rounded mr-2">
                                        {approval.data.dealer_code}
                                      </code>
                                    )}
                                    {approval.data.rm_code && (
                                      <code className="text-purple-400 bg-purple-500/10 px-1 rounded mr-2">
                                        {approval.data.rm_code}
                                      </code>
                                    )}
                                    {approval.data.email}
                                  </div>
                                </>
                              )}
                              {approval.type === 'target_override' && (
                                <span className="text-slate-300">Target Override Request</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="text-white">{approval.requested_by_name || 'Unknown'}</div>
                              {approval.requested_by_role && (
                                <div>{getRoleBadge(approval.requested_by_role)}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-400">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(approval.requested_at).toLocaleDateString()}
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(approval.status)}</TableCell>
                          <TableCell className="text-right">
                            {approval.status === 'pending' ? (
                              <div className="flex justify-end gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openDetailsDialog(approval)}
                                  className="text-slate-400 hover:text-white"
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => openActionDialog(approval, 'approve')}
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  title="Approve"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => openActionDialog(approval, 'reject')}
                                  className="bg-red-600 hover:bg-red-700"
                                  title="Reject"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <Button size="sm" variant="ghost" onClick={() => openDetailsDialog(approval)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredApprovals.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                            <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p>No approval requests found</p>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="bg-slate-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {selectedApproval && getTypeIcon(selectedApproval.type)}
              Request Details
            </DialogTitle>
          </DialogHeader>
          {selectedApproval && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-800/50 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">Type</span>
                  {getTypeBadge(selectedApproval.type)}
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Status</span>
                  {getStatusBadge(selectedApproval.status)}
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Requested By</span>
                  <span className="text-white">{selectedApproval.requested_by_name}</span>
                </div>
                {selectedApproval.requested_by_role && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Role</span>
                    {getRoleBadge(selectedApproval.requested_by_role)}
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-400">Date</span>
                  <span className="text-white">{new Date(selectedApproval.requested_at).toLocaleString()}</span>
                </div>
              </div>
              
              {selectedApproval.data && (
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <h4 className="text-sm font-medium text-slate-300 mb-3">Request Data</h4>
                  <div className="space-y-2 text-sm">
                    {selectedApproval.type === 'new_client' && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Client Code</span>
                          <code className="text-[#C5A059]">{selectedApproval.data.client_code}</code>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Client Name</span>
                          <span className="text-white">{selectedApproval.data.client_name}</span>
                        </div>
                        {selectedApproval.data.contact_number && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">Contact</span>
                            <span className="text-white">{selectedApproval.data.contact_number}</span>
                          </div>
                        )}
                        {selectedApproval.data.assigned_dealer_id && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">Assigned Dealer</span>
                            <span className="text-white">{selectedApproval.data.assigned_dealer_id}</span>
                          </div>
                        )}
                        {selectedApproval.data.assigned_rm_id && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">Assigned RM</span>
                            <span className="text-white">{selectedApproval.data.assigned_rm_id}</span>
                          </div>
                        )}
                      </>
                    )}
                    {(selectedApproval.type === 'new_dealer' || selectedApproval.type === 'new_rm') && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Name</span>
                          <span className="text-white">{selectedApproval.data.dealer_name || selectedApproval.data.name}</span>
                        </div>
                        {selectedApproval.data.dealer_code && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">Dealer Code</span>
                            <code className="text-green-400">{selectedApproval.data.dealer_code}</code>
                          </div>
                        )}
                        {selectedApproval.data.rm_code && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">RM Code</span>
                            <code className="text-purple-400">{selectedApproval.data.rm_code}</code>
                          </div>
                        )}
                        {selectedApproval.data.email && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">Email</span>
                            <span className="text-white">{selectedApproval.data.email}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
              
              {selectedApproval.remarks && (
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <h4 className="text-sm font-medium text-slate-300 mb-2">Remarks</h4>
                  <p className="text-white text-sm">{selectedApproval.remarks}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="bg-slate-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white capitalize flex items-center gap-2">
              {actionType === 'approve' ? (
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              ) : (
                <XCircle className="w-5 h-5 text-red-400" />
              )}
              {actionType} Request
            </DialogTitle>
            <DialogDescription>
              {actionType === 'approve' 
                ? 'Confirm approval of this request. The item will become active immediately.'
                : 'Provide a reason for rejection. The item will be marked as rejected.'
              }
            </DialogDescription>
          </DialogHeader>

          {selectedApproval && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-800/50 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-slate-400">Type:</div>
                  <div>{getTypeBadge(selectedApproval.type)}</div>
                  <div className="text-slate-400">Requested By:</div>
                  <div className="text-white">{selectedApproval.requested_by_name}</div>
                  <div className="text-slate-400">Date:</div>
                  <div className="text-white">
                    {new Date(selectedApproval.requested_at).toLocaleString()}
                  </div>
                  {selectedApproval.type === 'new_client' && selectedApproval.data && (
                    <>
                      <div className="text-slate-400">Client:</div>
                      <div className="text-white">
                        {selectedApproval.data.client_name} ({selectedApproval.data.client_code})
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm text-slate-400 mb-2 block">
                  Remarks {actionType === 'reject' && <span className="text-red-400">*</span>}
                </label>
                <Textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder={actionType === 'approve' ? 'Optional comments...' : 'Reason for rejection (required)...'}
                  className="bg-slate-800 border-white/10 text-white"
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowActionDialog(false)} className="text-slate-400">
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={processing || (actionType === 'reject' && !remarks.trim())}
              className={actionType === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : actionType === 'approve' ? (
                <CheckCircle className="w-4 h-4 mr-2" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              {actionType === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalsPage;
